import React from 'react';

import { Cloud, Mail } from 'lucide-react';

import { btn, card, NeutralPill, SettingsPageHeader, Switch } from './settingsV9';

import type { LucideIcon } from 'lucide-react';

// shortcut: every connector shows "Not connected" until Microsoft 365 (ROC-100) and Salesforce (ROC-101) ship.
const GROUPS: {
	title: string;
	connectors: {
		name: string;
		description: string;
		icon: LucideIcon;
		iconClass: string;
	}[];
}[] = [
	{
		title: 'Statement intake',
		connectors: [
			{
				name: 'Microsoft 365',
				description: 'Team sign-in and the statement inbox',
				icon: Mail,
				iconClass: 'bg-primary-50 text-primary-700',
			},
		],
	},
	{
		title: 'CRM',
		connectors: [
			{
				name: 'Salesforce',
				description:
					'Approved proposals, rates and PDF written to the opportunity',
				icon: Cloud,
				iconClass: 'bg-blue-100 text-blue-600',
			},
		],
	},
];

const IntegrationsTab: React.FC = () => (
	<div className="space-y-4">
		<SettingsPageHeader
			title={
				<>
					Integrations <NeutralPill>Admin only</NeutralPill>
				</>
			}
			subtitle="Every connector is set up once, the same way, and then works for every rep."
		/>
		<div className={`${card} overflow-hidden`}>
			{GROUPS.map((group) => (
				<React.Fragment key={group.title}>
					<div className="flex items-center gap-2.5 px-6 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold tracking-[.02em] uppercase text-gray-500">
						{group.title}
						<span className="font-medium normal-case tracking-normal text-gray-400">
							0 of {group.connectors.length} connected
						</span>
					</div>
					{group.connectors.map(
						({ name, description, icon: Icon, iconClass }) => (
							<div
								key={name}
								className="grid grid-cols-[minmax(300px,1.8fr)_150px_minmax(150px,1fr)_44px_150px] gap-4 items-center px-6 py-3.5 border-b border-gray-200 last:border-b-0"
							>
								<div className="flex items-center gap-3 min-w-0">
									<span
										className={`w-10 h-10 rounded-lg flex items-center justify-center flex-none ${iconClass}`}
									>
										<Icon className="h-5 w-5" />
									</span>
									<div className="min-w-0">
										<div className="text-sm font-semibold text-gray-900">
											{name}
										</div>
										<div className="text-xs text-gray-500 truncate">
											{description}
										</div>
									</div>
								</div>
								<div>
									<NeutralPill>
										<span className="w-1.5 h-1.5 rounded-full bg-current" />
										Not connected
									</NeutralPill>
								</div>
								<div className="text-xs text-gray-500">—</div>
								<div className="opacity-[.35] pointer-events-none">
									<Switch checked={false} disabled label={`Enable ${name}`} />
								</div>
								<div className="flex justify-end items-center gap-2">
									<button
										className={btn.primarySm}
										disabled
										title="Connecting is not available yet"
									>
										Connect
									</button>
									<span className="inline-block w-7" />
								</div>
							</div>
						),
					)}
				</React.Fragment>
			))}
		</div>
		<div className="text-xs text-gray-500">
			Connecting is not available yet. Microsoft 365 and Salesforce are being
			built and will appear here.
		</div>
	</div>
);

export default IntegrationsTab;
